import { GameInfo } from '@/features/games/shared/types/game-info';

export interface GamesResponse {
  games: GameInfo[];
}

export type FetchGamesOptions = {
  limit?: number;
  signal?: AbortSignal;
  /** Site locale for localized titles; the handler cannot read it itself (see the route). */
  locale?: string;
};

export class FetchGamesError extends Error {
  constructor(
    message: string,
    public status: number,
  ) {
    super(message);
    this.name = 'FetchGamesError';
  }
}

const SEARCH_ENDPOINT = '/api/game/search';

export const getSearchedGames = async (
  searchTerm: string,
  options: FetchGamesOptions = {},
): Promise<GamesResponse> => {
  const trimmedTerm = searchTerm.trim();

  if (trimmedTerm.length === 0) {
    return { games: [] };
  }

  const params = new URLSearchParams({
    searchTerm: trimmedTerm,
  });

  if (typeof options.limit === 'number') {
    params.set('limit', String(options.limit));
  }

  if (options.locale) {
    params.set('locale', options.locale);
  }

  const requestInit: RequestInit = {
    method: 'GET',
  };

  if (options.signal) {
    requestInit.signal = options.signal;
  }

  const response = await fetch(`${SEARCH_ENDPOINT}?${params.toString()}`, requestInit);

  if (!response.ok) {
    let message = 'Failed to fetch games search results';

    try {
      const body = (await response.json()) as Partial<GamesResponse> & { error?: string };
      if (typeof body.error === 'string') {
        message = body.error;
      }
    } catch {
      console.error('Failed to parse game search error response');
    }

    throw new FetchGamesError(message, response.status);
  }

  return (await response.json()) as GamesResponse;
};
